import { useState, useRef, useEffect } from 'react';

interface UseConfigWizardProps {
  initialData: any;
  onStepSave: (data: any, dirtyKeys: Set<string>) => Promise<void>;
  onFinish: (data: any) => Promise<void>;
  stepsCount: number;
}

export function useConfigWizard({ initialData, onStepSave, onFinish, stepsCount }: UseConfigWizardProps) {
  const enrichInitialData = (data: any) => ({
    ...(data || {}),
    roomLayoutsEnabled: data?.roomLayoutsEnabled ?? (Array.isArray(data?.roomLayouts) && data.roomLayouts.length > 0),
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(enrichInitialData(initialData));
  const formDataRef = useRef(enrichInitialData(initialData));
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isWizardFinished, setIsWizardFinished] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());

  // Quand initialData change (par ex. après un reload venant du composant parent), mettre à jour les valeurs SANS changer l'étape
  useEffect(() => {
    const enrichedData = enrichInitialData(initialData);
    setFormData(enrichedData);
    formDataRef.current = enrichedData;
    // Réinitialiser les dirtyKeys car les données viennent d'être rechargées depuis la DB
    setDirtyKeys(new Set());
  }, [initialData]);

  const updateFormData = (updates: any) => {
    setDirtyKeys((prev) => {
      const next = new Set(prev);
      Object.keys(updates).forEach((k) => next.add(k));
      return next;
    });
    setFormData((prev: any) => {
      const newData = { ...prev, ...updates };
      // Synchroniser la ref immédiatement — disponible dès le prochain tick
      formDataRef.current = newData;
      return newData;
    });
  };

  const handleNext = async () => {
    setSaveError(null);
    setCompletedSteps((prev) => new Set([...prev, currentStep]));

    if (currentStep < stepsCount - 1) {
      setCurrentStep(currentStep + 1);

      if (dirtyKeys.size > 0) {
        try {
          await onStepSave(formDataRef.current, dirtyKeys);
          setDirtyKeys(new Set());
        } catch (error) {
          setSaveError((error as Error).message || 'Erreur inconnue');
        }
      }
    } else {
      setIsPublishing(true);
      setIsSaving(true);
      try {
        await onFinish(formDataRef.current);
        setDirtyKeys(new Set());
        setToast({ type: 'success', message: 'Configuration sauvegardée avec succès !' });

        // Attendre que le toast soit visible avant de montrer la page de succès
        setTimeout(() => {
          setIsPublishing(false);
          setIsWizardFinished(true);
        }, 2500); // Un peu plus long pour l'animation
      } catch (error) {
        setSaveError((error as Error).message || 'Erreur inconnue');
        setToast({ type: 'error', message: 'Erreur lors de la sauvegarde.' });
        setIsPublishing(false);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    setSaveError(null);

    // Passer à l'étape suivante IMMÉDIATEMENT
    if (currentStep < stepsCount - 1) {
      setCurrentStep(currentStep + 1);
    }

    // Sauvegarder en arrière-plan si des modifications existent
    if (dirtyKeys.size > 0) {
      try {
        await onStepSave(formDataRef.current, dirtyKeys);
        setDirtyKeys(new Set());
      } catch (error) {
        console.error('❌ Erreur sauvegarde (skip):', error);
      }
    }
  };

  const handleStepClick = (index: number) => {
    // Permettre de revenir en arrière ou aller à une étape complétée
    if (index <= currentStep || completedSteps.has(index)) {
      setCurrentStep(index);
    }
  };

  const handleSaveCurrentStep = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      await onStepSave(formDataRef.current, dirtyKeys);
      setDirtyKeys(new Set());
      setToast({ type: 'success', message: 'Modifications sauvegardées avec succès !' });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Erreur inconnue';
      setSaveError(errorMessage);
      setToast({ type: 'error', message: `Erreur: ${errorMessage}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    setIsSaving(true);
    setSaveError(null);

    try {
      const publishedData = { ...formDataRef.current, isPublic: true };
      formDataRef.current = publishedData;
      await onFinish(publishedData);
      setDirtyKeys(new Set());
      setFormData(publishedData);
      setToast({ type: 'success', message: 'Espace publié avec succès !' });

      setTimeout(() => {
        setIsPublishing(false);
        setIsWizardFinished(true);
      }, 2500);
    } catch (error) {
      const errorMessage = (error as Error).message || 'Erreur inconnue';
      setSaveError(errorMessage);
      setToast({ type: 'error', message: `Erreur lors de la publication: ${errorMessage}` });
      setIsPublishing(false);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    currentStep,
    formData,
    completedSteps,
    isSaving,
    isPublishing,
    saveError,
    toast,
    isWizardFinished,
    setToast,
    updateFormData,
    handleNext,
    handlePrevious,
    handleSkip,
    handleStepClick,
    handleSaveCurrentStep,
    handlePublish,
  };
}
